import { useState, useEffect } from "react";
import { axiosInstance } from "../../lib/axios";
import toast from "react-hot-toast";
import { Save, AlertCircle, X, KeyRound, RefreshCw, Send, Loader2 } from "lucide-react";
import { useClientStore } from "../../store/useClientStore";
import Modal from "../ui/Modal";
import InputField from "../ui/InputField";
import SelectField from "../ui/SelectField";
import Button from "../ui/Button";
import CopyButton from "../ui/CopyButton";
import CredentialsModal, { type PortalCredentials } from "../projects/CredentialsModal";

const statusOptions = [
  { value: "Pending", label: "Pending" },
  { value: "Need to Contact", label: "Need to Contact" },
  { value: "Contacted - No Response", label: "Contacted - No Response" },
  { value: "Contact in Future", label: "Contact in Future" },
  { value: "Negotiated", label: "Negotiated" },
  { value: "Lost Lead", label: "Lost Lead" },
  { value: "Won Lead", label: "Won Lead" },
];

const sourceOptions = [
  { value: "Upwork - Irfan Baig", label: "Upwork - Irfan Baig" },
  { value: "Upwork - Nadeem", label: "Upwork - Nadeem" },
  { value: "Upwork - Farah", label: "Upwork - Farah" },
  { value: "Upwork - Rizwan", label: "Upwork - Rizwan" },
  { value: "Website - LogoGuts", label: "Website - LogoGuts" },
  { value: "Website - Multiversesol", label: "Website - Multiversesol" },
  { value: "Website - e360Digital", label: "Website - e360Digital" },
  { value: "Other", label: "Other" },
];
const tierOptions = [
  { value: "Premium", label: "Premium" },
  { value: "Standard", label: "Standard" },
  { value: "Basic", label: "Basic" },
];
const contactTypeOptions = [
  { value: "Individual", label: "Individual" },
  { value: "Agency", label: "Agency" },
];
const timezoneOptions = [
  { value: "", label: "Select time zone (optional)" },
  ...((typeof (Intl as any).supportedValuesOf === "function"
    ? (Intl as any).supportedValuesOf("timeZone")
    : ["UTC", "America/New_York", "America/Los_Angeles", "Europe/London", "Asia/Karachi", "Asia/Dubai"]
  ).map((z: string) => ({ value: z, label: z.replace(/_/g, " ") }))),
];
const generatePassword = (length = 10) => {
  const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

const EditClientModal = ({ client, onClose }: { client: any; onClose: () => void }) => {
  const { updateClient, provisionPortal, regeneratePortal } = useClientStore();
  const [formData, setFormData] = useState(client);
  const [tenants, setTenants] = useState<{ _id: string; name: string }[]>([]);
  useEffect(() => {
    axiosInstance.get("/tenants").then((r) => setTenants(r.data?.tenants || [])).catch(() => {});
  }, []);
  const [showCredentials, setShowCredentials] = useState(false);
  const [generated, setGenerated] = useState<{email: string; password: string} | null>(null);

  // Portal management (provision / regenerate / resend).
  const [portalBusy, setPortalBusy] = useState<"" | "provision" | "regenerate" | "resend">("");
  const [portalCreds, setPortalCreds] = useState<{ credentials: PortalCredentials; emailSent?: boolean } | null>(null);
  const hasPortal = Boolean(client.userId || client.workspace?.provisioned);

  useEffect(() => setFormData(client), [client]);

  const doProvision = async () => {
    setPortalBusy("provision");
    const r = await provisionPortal(client._id);
    setPortalBusy("");
    if (r?.status === "created" && r.credentials) setPortalCreds({ credentials: r.credentials, emailSent: r.emailSent });
  };
  const doRegenerate = async (sendEmail: boolean) => {
    setPortalBusy(sendEmail ? "resend" : "regenerate");
    const r = await regeneratePortal(client._id, sendEmail);
    setPortalBusy("");
    if (r?.credentials) setPortalCreds({ credentials: r.credentials, emailSent: r.emailSent });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { ...formData };
    // Tenant travels as an id (it may arrive populated as an object).
    if (payload.tenant && typeof payload.tenant === "object") payload.tenant = payload.tenant._id;
    let newCreds = null;

    if (!client.email && formData.email) {
      payload.password = generatePassword();
      newCreds = { email: formData.email, password: payload.password };
    }

    try {
      await updateClient(client._id, payload);
      toast.success("Client updated!");
      if (newCreds) {
        setGenerated(newCreds);
        setShowCredentials(true);
      } else {
        onClose();
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  return (
    <>
      <Modal isOpen={true} onClose={onClose} title="Edit Contact">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <InputField label="Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
            <InputField label="Email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
            <InputField label="Phone" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
            <InputField label="Website" value={formData.website} onChange={e => setFormData({ ...formData, website: e.target.value })} />
            <div className="md:col-span-2">
              <InputField label="Company" value={formData.company} onChange={e => setFormData({ ...formData, company: e.target.value })} />
            </div>
            <InputField label="Location" value={(formData as any).location || ""} onChange={e => setFormData({ ...formData, location: e.target.value } as any)} placeholder="City, Country" />
            <SelectField label="Time Zone" value={(formData as any).timezone || ""} onChange={e => setFormData({ ...formData, timezone: e.target.value } as any)} options={timezoneOptions} />
            <SelectField label="Client Tier" value={formData.tier || "Standard"} onChange={e => setFormData({ ...formData, tier: e.target.value })} options={tierOptions} />
            <SelectField label="Contact Type" value={formData.contactType || "Individual"} onChange={e => setFormData({ ...formData, contactType: e.target.value })} options={contactTypeOptions} />
            <SelectField label="Lead Status" value={formData.leadStatus} onChange={e => setFormData({ ...formData, leadStatus: e.target.value })} options={statusOptions} />
            <SelectField label="Contact Status" value={(formData as any).contactStatus || "Lead"} onChange={e => setFormData({ ...formData, contactStatus: e.target.value } as any)} options={[{ value: "Lead", label: "Lead" }, { value: "Client", label: "Client" }]} />
            <div className="md:col-span-2">
              <SelectField label="Lead Source" value={formData.leadSource} onChange={e => setFormData({ ...formData, leadSource: e.target.value })} options={sourceOptions} />
            </div>
            <div className="md:col-span-2">
              <SelectField
                label="Brand / Company"
                value={typeof formData.tenant === "object" ? formData.tenant?._id || "" : formData.tenant || ""}
                onChange={e => setFormData({ ...formData, tenant: e.target.value })}
                options={[
                  { value: "", label: "Select the brand this client belongs to…" },
                  ...tenants.map((t: any) => ({ value: t._id, label: t.name })),
                ]}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes</label>
              <textarea className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2462b2]/20 focus:border-[#2462b2] resize-none h-28" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} />
            </div>
          </div>
          {/* Client Portal management */}
          <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-4">
            <div className="flex items-center gap-2 mb-1">
              <KeyRound size={15} className="text-[#0058c3]" />
              <h4 className="text-sm font-semibold text-gray-800">Client Portal</h4>
              <span className={`ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full ${hasPortal ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {hasPortal ? "Active" : "Not provisioned"}
              </span>
            </div>
            <p className="text-[11px] text-gray-500 mb-3">
              {hasPortal
                ? "Regenerate a temporary password, or resend the login invitation by email."
                : "Create a Client Workspace account so this contact can log into the portal."}
            </p>
            <div className="flex flex-wrap gap-2">
              {!hasPortal ? (
                <button type="button" onClick={doProvision} disabled={!!portalBusy}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0058c3] text-white text-[13px] font-semibold hover:bg-[#004b9e] disabled:opacity-50">
                  {portalBusy === "provision" ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} Provision portal
                </button>
              ) : (
                <>
                  <button type="button" onClick={() => doRegenerate(false)} disabled={!!portalBusy}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-[13px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    {portalBusy === "regenerate" ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Regenerate password
                  </button>
                  <button type="button" onClick={() => doRegenerate(true)} disabled={!!portalBusy || !formData.email}
                    title={!formData.email ? "Add an email first" : "Email new credentials"}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 text-[13px] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    {portalBusy === "resend" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Resend invitation
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit"><Save size={16} /> Save Changes</Button>
          </div>
        </form>
      </Modal>

      {portalCreds && (
        <CredentialsModal
          credentials={portalCreds.credentials}
          clientName={client.name}
          emailSent={portalCreds.emailSent}
          onClose={() => setPortalCreds(null)}
        />
      )}

      <Modal isOpen={showCredentials} onClose={() => { setShowCredentials(false); onClose(); }} title="Account Created">
        <div className="bg-gradient-to-r from-[#2462b2]/5 to-[#2462b2]/10 border border-[#2462b2]/20 rounded-xl p-5 mb-5">
          <p className="text-sm font-medium mb-2">Client Login:</p>
          <p><strong>Email:</strong> {generated?.email}</p>
          <p><strong>Password:</strong> {generated?.password}</p>
        </div>
        <CopyButton text={`Email: ${generated?.email}\nPassword: ${generated?.password}`} label="Credentials" />
      </Modal>
    </>
  );
};

export default EditClientModal;
